import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Users,
  Heart,
  Target,
  Eye,
  Award,
  ArrowRight,
  Linkedin,
  Mail,
} from "lucide-react";

const teamMembers = [
  {
    name: "Simon",
    role: "Founder & CEO",
    bio: "Fondateur de FREELANCEURS.io, Simon accompagne les freelances vers plus de revenus, moins de charges et plus de liberté.",
    image: "https://customer-assets.emergentagent.com/job_4097332f-fead-4e94-b42c-b7ae8901b812/artifacts/cmolkxr3_Team%20Freelanceurs%20-%20%20Simon%20.png",
  },
  {
    name: "Assia",
    role: "Resp. Opérationnel",
    bio: "Assia gère le quotidien de nos freelances avec rigueur et veille au bon fonctionnement de chacun.",
    image: "https://customer-assets.emergentagent.com/job_4097332f-fead-4e94-b42c-b7ae8901b812/artifacts/w9n3d9yg_Team%20Freelanceurs%20-%20%20Assia%20.png",
  },
  {
    name: "Marie",
    role: "Resp. Juridique",
    bio: "Marie s'assure que tous les aspects juridiques de votre activité sont parfaitement sécurisés.",
    image: "https://customer-assets.emergentagent.com/job_4097332f-fead-4e94-b42c-b7ae8901b812/artifacts/4cwvpokz_Team%20Freelanceurs%20-%20%20Marie%20.png",
  },
  {
    name: "Lamya",
    role: "Resp. Pôle Business",
    bio: "Lamya vous accompagne dans le développement commercial et la recherche de mission pour votre activité.",
    image: "https://customer-assets.emergentagent.com/job_4097332f-fead-4e94-b42c-b7ae8901b812/artifacts/2rz4tjqh_Team%20Freelanceurs%20-%20%20Lamya%20.png",
  },
  {
    name: "Benjamin",
    role: "Business Development",
    bio: "Benjamin développe les partenariats et opportunités pour enrichir les offres de missions disponibles pour nos freelances.",
    image: "https://customer-assets.emergentagent.com/job_4097332f-fead-4e94-b42c-b7ae8901b812/artifacts/f7pg2l26_Team%20Freelanceurs%20-%20%20Benjamin%20.png",
  },
  {
    name: "Abdalla",
    role: "Support Gestion",
    bio: "Abdalla assure le support quotidien et la gestion administrative de nos clients freelances.",
    image: "https://customer-assets.emergentagent.com/job_4097332f-fead-4e94-b42c-b7ae8901b812/artifacts/b04b8g39_Team%20Freelanceurs%20-%20%20Abdallah%20.png",
  },
  {
    name: "Loic",
    role: "Expert Comptable",
    bio: "Loic apporte son expertise comptable pour optimiser la situation financière de chaque freelance.",
    image: "https://customer-assets.emergentagent.com/job_4097332f-fead-4e94-b42c-b7ae8901b812/artifacts/ijgl69l5_Team%20Freelanceurs%20-%20Loic%20%20.png",
  },
  {
    name: "Cyril",
    role: "Expert Immo / Financement",
    bio: "Cyril conseille nos clients sur les stratégies d'investissement immobilier et financier.",
    image: "https://customer-assets.emergentagent.com/job_4097332f-fead-4e94-b42c-b7ae8901b812/artifacts/vak4oovc_Team%20Freelanceurs%20-%20%20Cyril%20.png",
  },
];

const values = [
  {
    icon: Heart,
    title: "100% gagnent plus",
    description: "Tous nos freelances augmentent leurs revenus nets grâce à notre accompagnement 360° personnalisé.",
  },
  {
    icon: Target,
    title: "100% paient moins",
    description: "Optimisation fiscale et sociale pour réduire vos charges et maximiser ce qui vous reste.",
  },
  {
    icon: Eye,
    title: "Transparence totale",
    description: "Aucune mauvaise surprise : tarifs clairs, tout est inclus, le prix de la sérénité.",
  },
  {
    icon: Award,
    title: "Communauté Freelance",
    description: "Slack, événements, réseau d'experts : rejoignez une communauté de freelances qui réussissent.",
  },
];

const milestones = [
  { year: "2022", event: "Création de Freelanceurs.io" },
  { year: "2023", event: "Lancement de l'offre Freelanceurs 360°" },
  { year: "2024", event: "1er séminaire Freelanceurs.io à Paris en Juin" },
  { year: "2025", event: "Lancement du Pôle Business, pour la recherche de missions" },
  { year: "2026", event: "Modernisation et intégration de l'IA dans nos outils" },
];

export default function AProposPage() {
  const revealRefs = useRef([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible");
          }
        });
      },
      { threshold: 0.1 }
    );

    revealRefs.current.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, []);

  const addToRefs = (el) => {
    if (el && !revealRefs.current.includes(el)) {
      revealRefs.current.push(el);
    }
  };

  return (
    <div className="overflow-hidden" data-testid="a-propos-page">
      {/* Hero Section */}
      <section className="py-20 md:py-32 bg-white" data-testid="about-hero">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-[#52fbb0]/10 text-slate-700 px-4 py-2 rounded-full text-sm font-medium mb-6 animate-fade-in">
                <Users className="h-4 w-4" />
                À propos
              </div>
              <h1 className="font-manrope font-bold text-4xl md:text-5xl lg:text-6xl text-slate-900 mb-6 animate-fade-in-up">
                Freelanceurs<span className="gradient-text">.io</span>
              </h1>
              <p className="text-slate-600 text-lg md:text-xl leading-relaxed animate-fade-in-up delay-200">
                Notre mission : vous offrir un accompagnement 360 pour vous décharger de la gestion, 
                maximiser la performance de votre activité et vous permettre de vous recentrer sur l'essentiel ; 
                vous, vos proches et votre business.
              </p>
            </div>
            <div className="relative animate-fade-in delay-300">
              <img
                src="https://images.unsplash.com/photo-1768659347532-74d3b1efb0ae?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBvZmZpY2UlMjB0ZWFtJTIwbWVldGluZyUyMGNhc3VhbHxlbnwwfHx8fDE3NzA4MTM4OTl8MA&ixlib=rb-4.1.0&q=85"
                alt="L'équipe Freelanceurs.io"
                className="rounded-3xl shadow-xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-2xl shadow-xl">
                <div className="text-3xl font-bold text-[#52fbb0]">10+</div>
                <div className="text-slate-600">années d'expertise</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 md:py-32 bg-slate-50" data-testid="values-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="font-manrope font-bold text-3xl md:text-4xl lg:text-5xl text-slate-900 mb-4">
              Nos engagements
            </h2>
            <p className="text-slate-600 text-lg max-w-2xl mx-auto">
              Les principes qui guident notre accompagnement au quotidien.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <Card
                key={value.title}
                className="feature-card border-0 bg-white animate-fade-in"
                style={{ animationDelay: `${index * 100}ms` }}
                data-testid={`value-card-${index}`}
              >
                <CardContent className="p-8 text-center">
                  <div className="w-18 h-18 bg-[#52fbb0]/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                    <value.icon className="h-10 w-10 text-[#52fbb0]" />
                  </div>
                  <h3 className="font-manrope font-semibold text-xl text-slate-900 mb-3">
                    {value.title}
                  </h3>
                  <p className="text-slate-600">{value.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      <section className="py-20 md:py-32 bg-white" data-testid="timeline-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="font-manrope font-bold text-3xl md:text-4xl lg:text-5xl text-slate-900 mb-4">
              Notre histoire
            </h2>
            <p className="text-slate-600 text-lg max-w-2xl mx-auto">
              Les étapes clés de l'aventure.
            </p>
          </div>

          <div className="max-w-3xl mx-auto">
            <div className="relative">
              {/* Timeline line */}
              <div className="absolute left-8 md:left-1/2 top-0 bottom-0 w-0.5 bg-slate-200 -translate-x-1/2"></div>
              
              {milestones.map((milestone, index) => (
                <div
                  key={milestone.year}
                  className={`relative flex items-center gap-8 mb-12 animate-fade-in ${
                    index % 2 === 0 ? "md:flex-row" : "md:flex-row-reverse"
                  }`}
                  style={{ animationDelay: `${index * 150}ms` }}
                >
                  <div className={`flex-1 ${index % 2 === 0 ? "md:text-right" : "md:text-left"} hidden md:block`}>
                    <div className="font-manrope font-bold text-2xl text-[#52fbb0]">{milestone.year}</div>
                    <p className="text-slate-600 mt-1">{milestone.event}</p>
                  </div>
                  <div className="w-16 h-16 bg-[#52fbb0] rounded-full flex items-center justify-center z-10 flex-shrink-0">
                    <span className="font-manrope font-bold text-slate-900">{milestone.year.slice(-2)}</span>
                  </div>
                  <div className="flex-1 md:hidden">
                    <div className="font-manrope font-bold text-xl text-[#52fbb0]">{milestone.year}</div>
                    <p className="text-slate-600 mt-1">{milestone.event}</p>
                  </div>
                  <div className="flex-1 hidden md:block"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20 md:py-32 bg-slate-50" data-testid="team-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="font-manrope font-bold text-3xl md:text-4xl lg:text-5xl text-slate-900 mb-4">
              Notre équipe
            </h2>
            <p className="text-slate-600 text-lg max-w-2xl mx-auto">
              Des experts passionnés, à votre service pour votre réussite freelance.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {teamMembers.map((member, index) => (
              <Card
                key={member.name}
                className="border-0 shadow-lg overflow-hidden group animate-fade-in"
                style={{ animationDelay: `${index * 100}ms` }}
                data-testid={`team-card-${index}`}
              >
                <div className="aspect-square overflow-hidden">
                  <img
                    src={member.image}
                    alt={member.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <CardContent className="p-6">
                  <h3 className="font-manrope font-semibold text-lg text-slate-900">{member.name}</h3>
                  <p className="text-[#52fbb0] text-sm font-medium mb-3">{member.role}</p>
                  <p className="text-slate-600 text-sm">{member.bio}</p>
                  <div className="flex gap-2 mt-4">
                    <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full hover:bg-[#52fbb0]/10">
                      <Linkedin className="h-4 w-4 text-slate-500" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full hover:bg-[#52fbb0]/10">
                      <Mail className="h-4 w-4 text-slate-500" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-32 bg-slate-900" data-testid="about-cta">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="font-manrope font-bold text-3xl md:text-4xl lg:text-5xl text-white mb-6">
              Prêt à nous rejoindre ?
            </h2>
            <p className="text-slate-300 text-lg md:text-xl mb-10">
              Rejoignez les freelances qui gagnent plus et paient moins avec FREELANCEURS.io.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/simulateur" data-testid="about-simulateur-cta">
                <Button className="bg-[#52fbb0] text-slate-900 hover:bg-[#34d399] font-bold rounded-full px-10 py-6 text-lg">
                  Essayer le simulateur
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/contact" data-testid="about-contact-cta">
                <Button variant="outline" className="border-2 border-white/20 text-white hover:bg-white/10 font-semibold rounded-full px-10 py-6 text-lg">
                  Nous contacter
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
